import { HeaderTestIds } from '@trello/webdriverio-tests';
import React, { Suspense, FunctionComponent, useCallback } from 'react';
import { IconName } from 'app/common/components/controls/icon';
import { Noun, Verb, trackUe } from '@trello/analytics';
import HeaderButton from './button';
import { usePopover, Popover, PopoverScreen } from '@trello/nachos';
import { forNamespace } from 'app/common/lib/util/i18n';
import { TeamType } from '../../modules/state/models/teams';
import { useLazyComponent } from '@trello/use-lazy-component';

const viewTitle = forNamespace('view title');

enum Screen {
  Menu,
  CreateEnterpriseTeam,
}

export const CreateMenuButton: FunctionComponent = () => {
  const { triggerRef, toggle, hide, popoverProps, push } = usePopover<
    HTMLButtonElement
  >({
    initialScreen: Screen.Menu,
  });

  const onClick = useCallback(() => {
    trackUe({
      category: Noun.HEADER,
      verb: Verb.OPENS,
      directObj: Noun.ADD_MENU,
      method: 'by clicking header add menu button',
    });
    toggle();
  }, [toggle]);

  const HeaderCreateMenuPopover = useLazyComponent(() =>
    import(
      /* webpackChunkName: "create-menu-popover" */ 'app/gamma/src/components/popovers/header-create-menu'
    ),
  );

  const CreateTeamPopover = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "create-team-popover" */ 'app/gamma/src/components/popovers/create-team'
      ),
    {
      preload: popoverProps.isVisible,
      namedImport: 'CreateTeamPopover',
    },
  );

  return (
    <>
      <HeaderButton
        icon={IconName.Add}
        onClick={onClick}
        ref={triggerRef}
        testId={HeaderTestIds.CreateMenuButton}
      />
      <Popover
        {...popoverProps}
        title={viewTitle('create')}
        testId={HeaderTestIds.CreateMenuPopover}
      >
        <Suspense fallback={null}>
          <PopoverScreen id={Screen.Menu} noHorizontalPadding>
            <HeaderCreateMenuPopover
              onClickCreateEnterpriseTeam={() =>
                push(Screen.CreateEnterpriseTeam)
              }
              onCreateTeamOverlayShown={() => hide()}
              onCreateBoardOverlayShown={() => hide()}
            />
          </PopoverScreen>
          <PopoverScreen
            id={Screen.CreateEnterpriseTeam}
            title={viewTitle('create organization')}
          >
            <CreateTeamPopover teamType={TeamType.Enterprise} />
          </PopoverScreen>
        </Suspense>
      </Popover>
    </>
  );
};
